import { Link } from 'react-router-dom';
import {
  Package, Truck, Award, Snowflake, Check, ArrowRight, Download, Building2, Store, ShoppingBag, Ship,
} from 'lucide-react';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';
import useReveal from '@/hooks/useReveal';

const TIERS = [
  {
    slug: 'boutique',
    name: 'Boutique',
    range: '1 – 50 kg / week',
    ideal: 'Café · Small restaurant · Deli · Boutique retail',
    price_note: 'Standard trade price',
    features: [
      'Dedicated trade order line',
      'Weekly delivery slots (Wed + Fri)',
      'Malaga workshop pickup — free',
      'Minimum order $200',
      'Cut-to-spec on request',
    ],
  },
  {
    slug: 'growth',
    name: 'Growth',
    range: '50 – 500 kg / week',
    ideal: 'Restaurant group · Butcher shop · Supermarket line',
    price_note: 'Volume discount tiered',
    features: [
      'Everything in Boutique, plus',
      'Assigned account manager',
      'Priority Wed + Fri delivery',
      'Cryovac + tray-pack options',
      'Custom cutting sheets',
      '14-day trading terms (on approval)',
    ],
    featured: true,
  },
  {
    slug: 'enterprise',
    name: 'Enterprise',
    range: '500 kg+ / week',
    ideal: 'Hospitality group · Chain · Manufacturer · Export',
    price_note: 'Custom negotiated pricing',
    features: [
      'Everything in Growth, plus',
      'Private-label programme',
      'Halal + AQIS export certificates',
      '30-day trading terms (on approval)',
      'Interstate + international freight',
      'Dedicated cold-chain runs',
    ],
  },
];

const DELIVERY = [
  {
    icon: Store,
    label: 'Malaga Pickup',
    sub: 'Free · Mon–Fri 9am – 4pm',
    body: '15 Action Rd, Malaga WA 6090. Order by 12pm the day before.',
  },
  {
    icon: Truck,
    label: 'Perth Metro',
    sub: '$15 flat · Free over $199 · Wed + Fri',
    body: 'Refrigerated 4°C cold-chain, AM (9–12) or PM (1–5) windows.',
  },
  {
    icon: Package,
    label: 'Regional WA',
    sub: '$45 flat · Free over $350 · Fri only',
    body: 'AM (9–12) or PM (1–5) windows. Trade volumes eligible for weekly dedicated runs.',
  },
  {
    icon: Ship,
    label: 'Export Freight',
    slug: 'export',
    sub: 'AU · MY · SG · UAE',
    body: 'AQIS/DAFF registered facility. Halal + export certificates included.',
  },
];

const AUDIENCE = [
  { icon: Building2, label: 'Restaurants', desc: 'Fine-dining · Cafés · Caterers · Hotels' },
  { icon: ShoppingBag, label: 'Supermarkets', desc: 'Tray-pack · Cryovac · Private label' },
  { icon: Store, label: 'Retail Butchers', desc: 'Primals · Portioned · Custom cuts' },
  { icon: Ship, label: 'Export Partners', desc: 'AU · MY · SG · UAE — full docs' },
];

const CERTS = [
  { icon: Award, label: 'Halal Certified', desc: 'End-to-end segregated cold chain' },
  { icon: Snowflake, label: 'Cold-Chain', desc: '4°C chilled / −18°C frozen' },
  { icon: Award, label: 'AQIS Export', desc: 'DAFF-registered facility' },
  { icon: Award, label: 'Traceability', desc: 'Farm-to-fork on every carton' },
];

const TRADE_SHEET_URL = `${process.env.REACT_APP_BACKEND_URL}/api/trade-sheet.pdf`;
const CATALOGUE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/catalogue.pdf`;


export default function Wholesale() {
  const reveal = useReveal();

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="section-light App relative">
      <Seo
        title="Wholesale Meat Supply · Trade Pricing"
        description="Tier-priced wholesale butchery for Perth restaurants, cafés, retailers and food manufacturers. MSA-graded beef, WA lamb, free-range poultry and house-cured smallgoods. Halal certified, cold-chain delivered."
        keywords="wholesale butcher Perth, restaurant meat supplier Perth, halal wholesale WA, trade pricing meat, food service butchery Malaga, café meat supplier"
        path="/wholesale"
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          serviceType: 'Wholesale meat supply',
          provider: { '@type': 'FoodEstablishment', '@id': 'https://themeatworks.com.au/#business' },
          areaServed: [
            { '@type': 'AdministrativeArea', name: 'Western Australia' },
            { '@type': 'Country', name: 'Australia' },
          ],
          audience: { '@type': 'BusinessAudience', audienceType: 'Restaurants, cafés, retailers, food manufacturers' },
          description: 'Tier-priced wholesale butchery — Boutique, Trade and Enterprise. Halal certified, cold-chain delivered.',
        }}
      />
      <Navbar />

      {/* Hero — warm cream editorial */}
      <section
        id="top"
        data-testid="wholesale-hero"
        className="relative pt-20 sm:pt-28 pb-16 sm:pb-20 overflow-hidden bg-gradient-to-b from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA]"
      >
        <div
          aria-hidden
          className="absolute -top-40 left-1/2 -translate-x-1/2 w-[680px] h-[680px] bg-[var(--mw-primary)]/[0.05] rounded-full blur-[140px]"
        />
        <div ref={reveal} className="mw-reveal relative max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <div className="flex items-center gap-3 mb-8 justify-center">
            <span className="block w-10 h-px bg-black/25" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-black/60">
              Trade &middot; Wholesale &middot; Export
            </span>
            <span className="block w-10 h-px bg-black/25" />
          </div>
          <h1
            data-testid="wholesale-headline"
            className="font-display font-light leading-[0.98] tracking-tight text-black text-4xl sm:text-6xl lg:text-7xl xl:text-[6rem] max-w-5xl mx-auto"
          >
            One supplier.
            <br className="hidden sm:block" />
            <em className="not-italic text-[var(--mw-primary)]">Every trade partnership.</em>
          </h1>
          <p className="mx-auto mt-8 max-w-2xl text-black/70 text-base sm:text-lg leading-relaxed">
            Halal-certified. MSA-graded. Malaga-cut. Trade pricing tiers built for cafés, restaurant groups,
            supermarkets, chains, food manufacturers and export partners across AU · MY · SG · UAE.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-xl mx-auto">
            <a
              href="#tiers"
              data-testid="wholesale-cta-tiers"
              onClick={scrollTo('tiers')}
              style={{ color: '#fff' }}
              className="group flex-1 inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
            >
              See pricing tiers
              <ArrowRight size={14} />
            </a>
            <a
              href={TRADE_SHEET_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wholesale-cta-download"
              className="group flex-1 inline-flex items-center justify-center gap-2 border border-black/25 hover:border-black text-black text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
            >
              <Download size={14} />
              Trade sheet (PDF)
            </a>
          </div>
        </div>
      </section>

      {/* Audience */}
      <section className="section-light py-14 sm:py-20 bg-[#FAFAFA] border-t border-black/5">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex items-center gap-3 justify-center mb-8">
            <span className="block w-8 h-px bg-black/25" />
            <span className="text-[10px] uppercase tracking-[0.32em] text-black/55">Built for</span>
            <span className="block w-8 h-px bg-black/25" />
          </div>
          <div
            data-testid="wholesale-audience-grid"
            className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5"
          >
            {AUDIENCE.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.label}
                  className="flex flex-col gap-3 border border-black/10 bg-white p-5 sm:p-6 hover:border-[var(--mw-primary)] transition-colors shadow-[0_8px_30px_-16px_rgba(0,0,0,0.15)]"
                >
                  <Icon size={22} className="text-[var(--mw-primary)]" />
                  <div className="font-display text-xl sm:text-2xl text-black leading-none tracking-tight">
                    {a.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-black/55 leading-snug">
                    {a.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing tiers */}
      <section id="tiers" className="section-light py-20 sm:py-28 bg-white border-t border-black/5">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
              Trade pricing tiers
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-black">
              Priced for <em className="not-italic text-[var(--mw-primary)]">your volume</em>.
            </h2>
            <p className="mt-6 text-black/65 leading-relaxed">
              Tell us your weekly kilos and we&apos;ll match you to the right tier — with terms, packaging and
              delivery cadence that fit how you actually run.
            </p>
          </div>

          <div
            data-testid="wholesale-tiers-grid"
            className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8"
          >
            {TIERS.map((t) => (
              <article
                key={t.slug}
                data-testid={`wholesale-tier-${t.slug}`}
                style={t.featured ? { color: '#fff' } : {}}
                className={`relative flex flex-col p-8 sm:p-10 border transition-shadow ${
                  t.featured
                    ? 'bg-[#0A0A0A] border-transparent shadow-[0_20px_60px_-20px_rgba(155,17,30,0.4)]'
                    : 'bg-white border-black/10 shadow-[0_12px_40px_-24px_rgba(0,0,0,0.2)] hover:shadow-[0_20px_60px_-30px_rgba(0,0,0,0.3)]'
                }`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--mw-primary)] px-3 py-1 text-[9px] uppercase tracking-[0.28em]" style={{ color: '#fff' }}>
                    Most partners
                  </div>
                )}
                <div
                  style={t.featured ? { color: 'rgba(255,255,255,0.6)' } : {}}
                  className={`text-[10px] uppercase tracking-[0.28em] mb-3 ${t.featured ? '' : 'text-black/50'}`}
                >
                  Tier
                </div>
                <div className="font-display text-3xl sm:text-4xl leading-none tracking-tight mb-2">
                  {t.name}
                </div>
                <div className="font-display text-lg mt-1 text-[var(--mw-primary)]">
                  {t.range}
                </div>
                <div
                  style={t.featured ? { color: 'rgba(255,255,255,0.55)' } : {}}
                  className={`text-[10px] uppercase tracking-[0.22em] mt-1 ${t.featured ? '' : 'text-black/55'}`}
                >
                  {t.ideal}
                </div>

                <div className={`mt-6 pt-6 border-t ${t.featured ? 'border-white/15' : 'border-black/10'}`}>
                  <div
                    style={t.featured ? { color: 'rgba(255,255,255,0.55)' } : {}}
                    className={`text-[10px] uppercase tracking-[0.28em] mb-4 ${t.featured ? '' : 'text-black/50'}`}
                  >
                    {t.price_note}
                  </div>
                  <ul className="space-y-3">
                    {t.features.map((f) => (
                      <li
                        key={f}
                        style={t.featured ? { color: '#fff' } : {}}
                        className="flex items-start gap-3 text-sm leading-snug"
                      >
                        <Check size={16} className="shrink-0 mt-0.5 text-[var(--mw-primary)]" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to={`/wholesale/apply?tier=${t.slug}`}
                  data-testid={`wholesale-tier-cta-${t.slug}`}
                  style={t.featured ? { color: '#fff' } : {}}
                  className={`mt-8 inline-flex items-center justify-center gap-2 text-[11px] uppercase tracking-[0.24em] py-3 transition-colors ${
                    t.featured
                      ? 'bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)]'
                      : 'border border-black/25 hover:border-[var(--mw-primary)] hover:text-[var(--mw-primary)] text-black'
                  }`}
                >
                  Apply for this tier
                  <ArrowRight size={12} />
                </Link>
              </article>
            ))}
          </div>

          <div className="mt-10 text-center text-xs text-black/50">
            All prices ex-GST. Trading terms subject to credit approval. Volume, custom cuts and export
            certificates negotiated per account.
          </div>
        </div>
      </section>

      {/* Delivery & fulfilment */}
      <section
        id="delivery"
        data-testid="wholesale-delivery"
        className="section-light py-20 sm:py-28 bg-[#FAFAFA] border-t border-black/5"
      >
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
              Delivery terms
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[0.98] tracking-tight text-black">
              Cold-chain <em className="not-italic text-[var(--mw-primary)]">from plant to your dock</em>.
            </h2>
            <p className="mt-6 text-black/65 leading-relaxed">
              4°C refrigerated. Cut-off 12:00 pm Perth time the day before your slot. Bespoke runs for
              enterprise tier.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {DELIVERY.map((d) => {
              const Icon = d.icon;
              return (
                <div
                  key={d.label}
                  id={d.slug || undefined}
                  className="flex flex-col gap-3 bg-white border border-black/10 p-6 sm:p-7 scroll-mt-24"
                >
                  <Icon size={22} className="text-[var(--mw-primary)]" />
                  <div className="font-display text-xl text-black leading-none tracking-tight">
                    {d.label}
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--mw-primary)] leading-snug">
                    {d.sub}
                  </div>
                  <p className="text-sm text-black/65 leading-relaxed mt-1">
                    {d.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications strip */}
      <section
        data-testid="wholesale-certs"
        className="section-light py-14 sm:py-16 bg-white border-t border-black/5"
      >
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-10">
            {CERTS.map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.label} className="flex items-start gap-4">
                  <Icon size={26} className="text-[var(--mw-primary)] shrink-0 mt-0.5" />
                  <div>
                    <div className="font-display text-lg text-black leading-none tracking-tight">
                      {c.label}
                    </div>
                    <div className="mt-1.5 text-[10px] uppercase tracking-[0.22em] text-black/55 leading-snug">
                      {c.desc}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Trade sheet CTA banner — cream editorial */}
      <section
        data-testid="wholesale-sheet-banner"
        className="relative py-20 sm:py-24 bg-gradient-to-br from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA] text-black border-t border-black/10 overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-y-0 right-0 w-1/2 bg-gradient-to-l from-[var(--mw-primary)]/[0.10] to-transparent"
        />
        <div className="relative max-w-screen-2xl mx-auto px-6 sm:px-10 lg:px-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
              Trade sheet &middot; PDF
            </div>
            <h2 className="font-display font-light text-4xl sm:text-5xl leading-[0.98] tracking-tight text-black">
              The full range,
              <br />
              <em className="not-italic text-[var(--mw-primary)]">in your inbox.</em>
            </h2>
            <p className="mt-6 text-black/70 leading-relaxed max-w-md">
              A 2-page trade sheet with pricing tiers, delivery terms and how to apply — 200 KB, perfect
              to share with your purchasing team. Or grab the full 118-cut catalogue.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 lg:justify-end">
            <a
              href={TRADE_SHEET_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wholesale-sheet-download"
              style={{ color: '#fff' }}
              className="inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors"
            >
              <Download size={14} />
              Trade Sheet (2 pages)
            </a>
            <a
              href={CATALOGUE_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="wholesale-catalogue-download"
              className="inline-flex items-center justify-center gap-2 border border-black/25 hover:border-black text-black px-8 py-4 text-xs uppercase tracking-[0.22em] transition-colors"
            >
              <Download size={14} />
              Full Catalogue
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
